/**
 * Manuals & Guides hub — machine manuals assembled from live courses.
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authFetch } from '../utils/api';
import { Tile } from '../components/ui';
import { isResellerOrStaff, isInternalStaff } from '../config/navMenu';
import { layoutGuides } from './guide-groups';
import './UserGuides.css';

function manualPdfPageHref(setId, opts) {
  const qs = new URLSearchParams();
  qs.set('set', setId);
  if (opts && opts.course) qs.set('course', opts.course);
  if (opts && opts.phase) qs.set('phase', opts.phase);
  if (opts && opts.pack) qs.set('pack', opts.pack);
  return `/field-ops-manual-pdf.html?${qs.toString()}`;
}

const FAQ_SECTIONS = [
  {
    title: 'Safety stop',
    items: [
      {
        q: 'The safety reset lamp is flashing. What do I do?',
        courseId: 'pe320-drive-sto',
        courseName: 'PE320 Drive and Safety Stop',
        paragraphs: [
          'A flashing lamp means an emergency stop is still in. Twist every pressed stop out. The lamp goes solid blue when the circuit is clear. Opening or closing the cell door does not change this lamp.',
          'Then press safety reset. You should hear the contactors. Until you press it, the beds and heaters will not come ready.',
          'A pendant E-stop stops the robot only. The heaters stay on. Twist it out and acknowledge the message. Every other E-stop, on the Epicurus controller, the wall, or inside the cell, stops the robot and drops the heaters. The lamp keeps flashing until every one of those is reset.'
        ]
      },
      {
        q: 'What happens when the safety stop trips?',
        courseId: 'pe320-drive-sto',
        courseName: 'PE320 Drive and Safety Stop',
        paragraphs: [
          'Safe Torque Off takes torque off the motor. The extruder screw coasts straight away. It does not brake. That stop is there to prevent an unexpected start, not to make a tidy halt.',
          'The spindle, where the cell has one, gets a delayed stop: it comes to a stop, then torque is removed. The panel note on these cells records that delay as about 2 seconds. It depends on the risk assessment for the cell. Do not assume the spindle and the screw stop together.',
          'Every E-stop except the pendant also drops the extruder and bed heater contactor. Heat does not stay available through those. Do not enable the screw again until Epicurus shows the zones back where you expect them, and the pendant S lamp is green.',
          'Use the normal stop when you only want to pause a print. The red E-stop is for when someone is about to be hurt.'
        ]
      },
      {
        q: 'The pendant says the extruder is not ready.',
        courseId: 'pe320-preheat-soak',
        courseName: 'PE320 Preheat and Soak',
        paragraphs: [
          'Read the zones on Epicurus and the safety-reset lamp. The extruder is not ready until the zones have reached the material setpoint and you have pressed safety reset after the lamp is solid blue.',
          'A cold nozzle that was just fitted needs 10 to 15 minutes at temperature before you purge. If you purge while it is still cold, pressure can blow the burst plug. That plug is a one-use part.',
          'Do not jog or clear faults while the robot controller is still booting. Boot takes about 4 to 5 minutes. Red lights after an isolation are normal until the safeties are reset.'
        ]
      }
    ]
  },
  {
    title: 'Drives and faults',
    items: [
      {
        q: 'The green S is on. Can I move the robot?',
        courseId: 'pe320-cold-start',
        courseName: 'Cold Start and Hardware Initialisation',
        paragraphs: [
          'Green S means the background program is running. That is one of the checks before motion, not a motion enable by itself.',
          'Jog or load a program only when the robot has finished booting, S is green, the axes report ready, and Epicurus is up without a hardware fault. Stay in T1, with the enabling switch held, for the first moves.'
        ]
      },
      {
        q: 'Reset Drive does not clear the fault.',
        courseId: 'pe320-drive-sto',
        courseName: 'PE320 Drive and Safety Stop',
        paragraphs: [
          'Reset Drive is a short pulse, about half a second. Holding it down does not clear the fault. The drive only sees a new pulse after the line is released and pulsed again.',
          'Use tool 1, with no program running. The controller also tries that pulse by itself, up to three times, each time tool 1 is selected. Reset Attempts on that tab counts them. At 3 it stops. Deselect tool 1, select it again, and pulse once more.',
          'Do not press Reset Drive while a program is running. If the fault comes straight back, stop and call Rapid Fusion. Do not keep resetting it.'
        ]
      },
      {
        q: 'The drive shows B122.',
        courseId: 'pe320-drive-sto',
        courseName: 'PE320 Drive and Safety Stop',
        paragraphs: [
          'B122 means the drive and the robot are not staying in step. On these cells that has been a commissioning setting, not something to clear from the panel.',
          'Do not keep resetting it. Call Rapid Fusion. Read the code off the drive that has actually faulted. The extruder screw is a Schneider Lexium. The spindle, where fitted, is a Schneider Altivar. A code on one is not a code on the other.'
        ]
      },
      {
        q: 'What should the extrusion multiplier be?',
        courseId: 'pellet-print-fault-finding',
        courseName: 'Z offset and bead width',
        paragraphs: [
          'Leave Extrusion Mult at 100% unless a trainer has changed it for this bead. A bead about twice as fat on every move means it was left at 200%. Set it back to 100% before you change the slice.',
          'You can lower it for a few circuits to thin a fat first bead, then put it back to 100%. If you leave it down, every later layer is starved.'
        ]
      }
    ]
  },
  {
    title: 'Material, purge, and heat',
    items: [
      {
        q: 'How do I purge?',
        courseId: 'pe320-purge',
        courseName: 'PE320 Purge on the Robot',
        paragraphs: [
          'Do not purge until pellet feed is on, the dryer is running for this material, the sight glass shows that material, and every extruder zone has finished its soak.',
          'Put the robot in T1. Jog the head over a purge bucket. The purge program does not move the arm. On the pendant, Rapid Fusion, RF Extruder: purge speed 20, purge time 60 seconds. That speed uses the same units as the programmed print speed. Run longer if the stream has not cleared.',
          'S must be green and I ready. R grey means ready to run. Hold Back and Play together. Stay in T1 unless a trainer on this cell has cleared automatic.',
          'A good stream matches the loaded colour, flows steadily, and does not bubble, steam, or sound like it is popping. Purge again if melt has sat in the barrel for more than 30 minutes.'
        ]
      },
      {
        q: 'The pellets look damp.',
        courseId: 'pe320-material-feed',
        courseName: 'PE320 Material Feed',
        paragraphs: [
          'Dry them before you print. Wet pellets flash off as steam in the barrel. The purge looks foamy or fibrous, and the part is weak.',
          'Set the dryer from the bag label and the material card. Do not invent a temperature. Pellets are typically 3 to 5 mm. Mixed sizes, fines, or a contaminated bag make the bead wander.',
          'Supply air for the feed line is 6 to 8 bar, and it has to be clean and dry. Wet or oily air puts moisture back into pellets you have already dried. If the hopper lamp is off and the sight glass is empty, turn Dryer Feeding and Extruder Feeding on. Do not heat an empty screw to make up for missing pellets.'
        ]
      },
      {
        q: 'Can I run PETG at the polycarbonate temperatures?',
        courseId: 'pe320-nozzle-pellet-dryer-clean',
        courseName: 'Nozzle change, pellet change, and extruder clean',
        paragraphs: [
          'No. Do not take leftover PETG, or any low-temperature melt, up to polycarbonate temperatures. That hits the smoke point, carbonises the barrel, and can seize the screw.',
          'Hoover the dryer, tubes, hopper, and filters first. Purge with purge compound at the old temperature until the stream is the purge, not the old colour. Then raise the zones, soak about 25 minutes, and purge until the new colour is clean.',
          'A swap inside the same range, such as ABS and PETG, about 20 to 30 degrees, does not need purge compound. You still clean the system if the colour or the strength of the part matters.'
        ]
      },
      {
        q: 'The heaters are still on after the job.',
        courseId: 'apollo-ui-user-guide',
        courseName: 'Epicurus operator guide',
        paragraphs: [
          'A current job turns the heaters off when the program finishes, so a print that ends overnight does not sit at temperature. If the barrel and beds are still hot, that end-of-job step was not seen by the panel.',
          'Press Turn All Heaters Off on Epicurus. Tell Rapid Fusion. Do not leave the cell with the barrel hot.'
        ]
      },
      {
        q: 'What air pressure does the cell need?',
        courseId: 'pe320-material-feed',
        courseName: 'PE320 Material Feed',
        paragraphs: [
          'The Line Vac that pulls pellets into the dryer needs 6 to 8 bar. Below that, feed is intermittent. Older commissioning sheets still say 5 to 6 bar. Use 6 to 8 bar on site.',
          'The air has to stay clean and dry. If pellets are not arriving, check the pressure, that the pellet pipe is not kinked, and that the venturi is not clogged with dust.'
        ]
      }
    ]
  },
  {
    title: 'Starting a print',
    items: [
      {
        q: 'How do I load the job?',
        courseId: 'kuka-directory-loader',
        courseName: 'Load a job',
        paragraphs: [
          'Upload the program from the Epicurus panel first, under Programs. The panel unpacks the zip. KUKA does not take a USB drop.',
          'On the pendant Job tab, pick Load Job once. Wait until Loading goes out. A second load can clear the files you just uploaded. No program may be loaded. Select Job needs T1. Do not load mid-print. A load stops the screw and the spindle.',
          'The success line in the message window names the trigger this cell used. That number belongs to this cell. Do not type it into another robot.'
        ]
      },
      {
        q: 'When do I lock the door and switch to Auto?',
        courseId: 'kuka-safe-operation',
        courseName: 'KUKA safe operation',
        paragraphs: [
          'The first approach stays in T1, enabling switch held, at 5 to 10 percent, until the nozzle is clear of the bed and the first layer is sound.',
          'Then everyone leaves the cell. Take the pendant with you. Close the door and hold the lock for about two seconds, until it stops flashing. Enable the drives in T1 before you switch to Auto. Auto does not enable them.',
          'You cannot unlock the door while Auto is running. Stop first. If someone is locked inside, there is a release inside the cell. If the pendant is outside and that release does not open the door, they use the E-stop inside. That stop drops the heaters as well as the robot.',
          'Before you jog off a print, go up in Z only, in base, on the PE320 tool, in T1. Jogging sideways while you are still on the part drags the nozzle through it. Do not stop on an outside wall. Stop on infill or a back corner. ABS and nylon need a brim and toe clamps.'
        ]
      }
    ]
  }
];

const ZEUS_FAQ = {
  title: 'Zeus scan and cutters',
  items: [
    {
      q: 'The scan landed in the wrong place.',
      courseId: 'zeus-scan-to-mill',
      courseName: 'Zeus Scan to Mill',
      paragraphs: [
        'Capture with no tool and no base selected. On the pendant that is null frame and null tool. Read Robot, Display, Actual position before you capture.',
        'Hand-eye was done in no-tool and no-base, so the capture has to match. If a tool or a base is selected, the cloud is offset to that tip or that bed.',
        'Do not touch the part after the scan. If you flip it, scan it again. If the scanner was bumped, recalibrate before you mill. Do not mill from that cloud.'
      ]
    },
    {
      q: 'The cutter change will not run.',
      courseId: 'kuka-tool-dock-cutter-change',
      courseName: 'Tool change',
      paragraphs: [
        'Do not start a cutter change while the PE320 is the fitted head. Nothing on the pendant stops that. Dock the extruder first.',
        'The first runs stay at 25 or 50 percent until the approaches are familiar. One hundred percent is for after a clean run. If an approach looks wrong, release the enabling switch.',
        'The pocket names on the cutter list belong to that cell. A job calls the pocket number. Do not type those names into another robot. Unload Only puts the cutter back in the pocket it came from.'
      ]
    },
    {
      q: 'Is a spindle fault the same as a screw fault?',
      courseId: 'pe320-drive-sto',
      courseName: 'PE320 Drive and Safety Stop',
      paragraphs: [
        'No. The spindle is a Schneider Altivar. The extruder screw is a Schneider Lexium 32M, with a 25:1 gearbox. They do not stop the same way.',
        'Read the code off the drive that has actually faulted. Spindle codes are in the Altivar manual. Screw codes are in the Lexium manual. Both are linked from the drive and safety stop course. If the code is not in the operator table, call Rapid Fusion. Do not clear it and start again until you know which row you are in.'
      ]
    }
  ]
};

function formatDuration(hours) {
  const minutes = Math.round(Number(hours) * 60);
  if (!Number.isFinite(minutes) || minutes <= 0) return '';
  if (minutes < 60) return `${minutes} min`;
  if (minutes % 60 === 0) {
    const whole = minutes / 60;
    return `${whole} hr`;
  }
  return `${hours}h`;
}

function hoursLabel(h) {
  if (!h) return '';
  const parts = [];
  if (h.selfStudy != null) parts.push(`${formatDuration(h.selfStudy)} study`);
  if (h.quiz != null) parts.push(`${formatDuration(h.quiz)} quiz`);
  if (h.handsOn != null) parts.push(formatDuration(h.handsOn));
  return parts.filter(Boolean).join(' · ');
}

function courseDescription(course) {
  const bits = [];
  if (course.summary) bits.push(course.summary);
  bits.push(`${course.moduleCount || 0} modules`);
  if (course.updatedAt) {
    bits.push('Updated ' + new Date(course.updatedAt).toLocaleDateString('en-GB'));
  }
  const hours = hoursLabel(course.estimatedHours);
  if (hours) bits.push(hours);
  return bits.join(' · ');
}

function courseIsUpcoming(course) {
  return course && course.viewerState === 'upcoming';
}

function courseIsPreRelease(course) {
  return course && course.viewerState === 'pre-release';
}

function staffVisibilityPrefix(course) {
  const label = course && course.visibilityLabel;
  if (label === 'live') return 'Live. ';
  if (label === 'hidden') return 'Hidden. ';
  if (label === 'early-access' || (course && course.releaseStage !== 'released')) return 'Early access. ';
  return 'Live. ';
}

const UserGuides = ({ userRole, earlyAccess = false }) => {
  const isStaff = isInternalStaff(userRole);
  const showTechSupport = isResellerOrStaff(userRole);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sets, setSets] = useState([]);
  const [publishedFaqs, setPublishedFaqs] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    authFetch('/api/faq')
      .then((res) => (res.ok ? res.json() : { items: [] }))
      .then((data) => {
        if (!cancelled) {
          setPublishedFaqs((data.items || []).filter((item) => !item.status || item.status === 'published'));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('authToken');
    fetch('/api/field-ops/manuals', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include'
    })
      .then((res) => {
        if (!res.ok) throw new Error('Could not load manuals');
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setSets(data.manuals || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load manuals');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const requestedSet = searchParams.get('set');
  const selectedSet = sets.find((set) => set.setId === requestedSet) || sets[0] || null;

  function selectSet(setId) {
    setSearchParams({ set: setId }, { replace: true });
  }

  const layout = selectedSet ? layoutGuides(selectedSet.courses, selectedSet.platformId) : null;
  const faqSections = FAQ_SECTIONS.concat(
    selectedSet && selectedSet.setId === 'zeus' ? [ZEUS_FAQ] : []
  ).concat(publishedFaqs.length ? [{
    title: 'From support',
    items: publishedFaqs.map((item) => ({
      q: item.question,
      paragraphs: String(item.answer || '').split(/\n\n+/).filter(Boolean),
    })),
  }] : []);

  function courseTile(course) {
    return (
      <Tile
        key={course.courseId}
        icon="book-open"
        title={course.name}
        stepNumber={course.pathNumber || null}
        description={
          (isStaff
            ? staffVisibilityPrefix(course)
            : courseIsPreRelease(course)
              ? 'Pre-release. '
              : '') +
          courseDescription(course)
        }
        linkTo={`/field-ops-manual.html?set=${encodeURIComponent(selectedSet.setId)}&course=${encodeURIComponent(course.courseId)}`}
        linkText="Read manual"
        secondaryLinkTo={`/field-ops-course.html?id=${encodeURIComponent(course.courseId)}`}
        secondaryLinkText="Open course"
        extraLinks={[{
          to: manualPdfPageHref(selectedSet.setId, { course: course.courseId }),
          text: 'Download PDF',
          external: true,
          newTab: true,
          target: `manual-pdf-${selectedSet.setId}-${course.courseId}`
        }]}
        disabled={courseIsUpcoming(course)}
        disabledText="Upcoming"
      />
    );
  }

  return (
    <div className="rf-user-guides">
      <section className="rf-hero">
        <h1 className="rf-hero-title">
          <i className="fas fa-book"></i> Manuals &amp; Guides
        </h1>
        <p className="rf-hero-subtitle">
          {isStaff
            ? 'One manual per cell, assembled from the live courses. Each course is marked Live, Early access, or Hidden.'
            : earlyAccess
              ? 'Cell manuals are available as pre-release while we review them.'
              : 'Released manuals are open. Pre-release items show as Upcoming until we finish review.'}
        </p>
        <a className="rf-btn rf-btn-primary rf-guides-faq-jump" href="#common-questions">
          Common questions
        </a>
      </section>

      <div className="rf-user-guides-content">
        <div className="rf-container">
          {loading && <p>Loading manuals...</p>}
          {error && <p className="rf-guides-error">{error}</p>}

          {!loading && sets.length > 0 && (
            <div className="rf-tabs" role="tablist" aria-label="Cell manual">
              {sets.map((set) => {
                const active = selectedSet && selectedSet.setId === set.setId;
                return (
                  <button
                    key={set.setId}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`rf-tab ${active ? 'active' : ''}`}
                    onClick={() => selectSet(set.setId)}
                  >
                    {set.name}
                  </button>
                );
              })}
            </div>
          )}

          {selectedSet && (
            <section className="rf-section" key={selectedSet.setId}>
              <h2 className="rf-section-title">{selectedSet.title}</h2>
              <p className="rf-guides-set-summary">{selectedSet.summary}</p>
              <div className="rf-tiles-grid rf-guides-full-manual">
                <Tile
                  icon={30}
                  title={selectedSet.setId === 'sales'
                    ? 'Download the Sales manual'
                    : `Download the full ${selectedSet.name} operator manual`}
                  description={selectedSet.setId === 'sales'
                    ? 'Which system to recommend, the print-scan-mill loop, and the claims you may make.'
                    : 'Zero to first print, then Print hero. Zeus adds Scan to Mill. Commissioning is a separate download.'}
                  linkTo={`/field-ops-manual.html?set=${encodeURIComponent(selectedSet.setId)}`}
                  linkText="Read full manual"
                  secondaryLinkTo={manualPdfPageHref(selectedSet.setId, {})}
                  secondaryLinkText="Download PDF"
                  secondaryExternal
                  secondaryNewTab
                  secondaryTarget={`manual-pdf-${selectedSet.setId}-full`}
                  disabled={(selectedSet.courses || []).length > 0 && (selectedSet.courses || []).every(courseIsUpcoming)}
                  disabledText="Upcoming"
                />
              </div>

              {layout.phases.map((phase) => (
                <section key={phase.id} className="rf-guides-phase">
                  <div className="rf-guides-phase-bar">
                    <h3>{phase.title}</h3>
                    {selectedSet.setId !== 'sales' && (
                      <a
                        className="rf-btn rf-btn-secondary"
                        href={manualPdfPageHref(selectedSet.setId, { phase: phase.id })}
                        target={`manual-pdf-${selectedSet.setId}-${phase.id}`}
                      >
                        Download this section
                      </a>
                    )}
                  </div>
                  <div className="rf-tiles-grid">
                    {phase.courses.map(courseTile)}
                  </div>
                </section>
              ))}

              {layout.commissioning.length > 0 && (
                <section className="rf-guides-phase">
                  <div className="rf-guides-phase-bar">
                    <h3>Commissioning</h3>
                    <a
                      className="rf-btn rf-btn-secondary"
                      href={manualPdfPageHref(selectedSet.setId, { pack: 'commissioning' })}
                      target={`manual-pdf-${selectedSet.setId}-commissioning`}
                    >
                      Download commissioning
                    </a>
                  </div>
                  <p className="rf-guides-set-summary">For reseller and Rapid Fusion accounts.</p>
                  <div className="rf-tiles-grid">
                    {layout.commissioning.map(courseTile)}
                  </div>
                </section>
              )}
            </section>
          )}

          <section className="rf-section" id="common-questions">
            <h2 className="rf-section-title">Common questions</h2>
            <div className="rf-guides-faq">
              {faqSections.map((section) => (
                <details key={section.title} className="rf-guides-faq-section">
                  <summary>{section.title}</summary>
                  <div className="rf-guides-faq">
                    {section.items.map((row) => (
                      <details key={row.q} className="rf-guides-faq-item">
                        <summary>{row.q}</summary>
                        {row.paragraphs.map((text) => (
                          <p key={text}>{text}</p>
                        ))}
                        {row.courseId && (
                          <p>
                            <a href={`/field-ops-course.html?id=${encodeURIComponent(row.courseId)}`}>
                              {`Open ${row.courseName}`}
                            </a>
                          </p>
                        )}
                      </details>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {showTechSupport && (
            <section className="rf-section">
              <h2 className="rf-section-title">Product PDFs and vendor manuals</h2>
              <div className="rf-tiles-grid">
                <Tile
                  icon="screwdriver-wrench"
                  title="Vendor manuals (KUKA, Schneider, Duet)"
                  description="Browse the Epicurus Hardware Config library in Technical Support."
                  linkTo="/tech-support"
                  linkText="Open Technical Support"
                />
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserGuides;
